import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Bell,
  CheckCheck,
  ChevronDown,
  ArrowUpRight,
} from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import { safeApiErrorMessage } from "../utils/safeApiErrorMessage";

const SectionLabel = ({ children }) => (
  <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0a2540]/40">
    {children}
  </h2>
);

function timeAgo(dateString) {
  if (!dateString) return "";
  const diffMs = Date.now() - new Date(dateString).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateString).toLocaleDateString();
}

function NotificationCard({
  notification,
  isExpanded,
  onToggle,
  onViewTransfer,
}) {
  const unread = !notification.read_at;

  return (
    <div
      className={`w-full rounded-2xl border transition-all duration-200 overflow-hidden shadow-sm ${
        unread
          ? "bg-[#0A4730]/[0.03] border-[#0A4730]/15"
          : "bg-white border-[#0a2540]/5"
      }`}
    >
      <div className="flex items-center">
        {/* MAIN TAP AREA TO EXPAND/COLLAPSE */}
        <button
          onClick={onToggle}
          className="flex-1 flex items-center gap-3 p-3 md:p-4 active:bg-slate-50 transition text-left min-w-0"
        >
          <div className="relative shrink-0">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                unread ? "bg-[#0A4730]/10" : "bg-[#0A2540]/5"
              }`}
            >
              <Bell
                size={16}
                className={
                  unread
                    ? "text-[#0A4730]"
                    : "text-[#0A2540]/30"
                }
              />
            </div>

            {unread && (
              <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-[#0A4730] ring-2 ring-white" />
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p
                className={`text-sm leading-tight truncate ${
                  unread
                    ? "font-semibold text-[#0A2540]"
                    : "font-medium text-[#0A2540]/70"
                }`}
              >
                {notification.title}
              </p>

              <span className="text-[10px] text-[#0A2540]/35 whitespace-nowrap shrink-0">
                {timeAgo(notification.created_at)}
              </span>
            </div>

            {!isExpanded && notification.body && (
              <p className="text-xs text-[#0A2540]/45 mt-1 line-clamp-1">
                {notification.body}
              </p>
            )}
          </div>

          <ChevronDown
            size={15}
            className={`shrink-0 text-[#0A2540]/25 transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </button>

        {/* DIVIDER & QUICK ACTION */}
        {notification.transfer_id && (
          <>
            <div className="w-px h-8 bg-slate-100 shrink-0" />

            <button
              onClick={() =>
                onViewTransfer(notification.transfer_id)
              }
              className="px-4 py-3 h-full flex items-center justify-center text-[#0A4730] active:bg-slate-50 transition shrink-0"
              title="View transfer"
            >
              <ArrowUpRight
                size={15}
                strokeWidth={2.5}
              />
            </button>
          </>
        )}
      </div>

      {isExpanded && notification.body && (
        <div className="px-4 pb-4 pt-1 border-t border-[#0a2540]/5 bg-slate-50/50">
          <p className="text-xs text-[#0A2540]/70 leading-relaxed">
            {notification.body}
          </p>
        </div>
      )}
    </div>
  );
}

export function NotificationsPage() {
  const navigate = useNavigate();

  const {
    notifications,
    unreadCount,
    isLoading,
    markRead,
    markAllRead,
    isUpdating,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useNotifications();

  const [expandedId, setExpandedId] =
    useState(null);
  const [actionError, setActionError] =
    useState(null);

  const handleToggle = (notification) => {
    const nowExpanding =
      expandedId !== notification.id;

    setExpandedId(
      nowExpanding
        ? notification.id
        : null,
    );

    if (nowExpanding && !notification.read_at) {
      setActionError(null);

      markRead(notification.id).catch((err) => {
        setActionError(
          safeApiErrorMessage(
            err,
            "We couldn't mark this notification as read.",
          ),
        );
      });
    }
  };

  const handleMarkAllRead = async () => {
    setActionError(null);

    try {
      await markAllRead();
    } catch (err) {
      setActionError(
        safeApiErrorMessage(
          err,
          "We couldn't mark your notifications as read.",
        ),
      );
    }
  };

  const handleViewTransfer = (transferId) => {
    navigate(`/transaction/${transferId}`);
  };

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-24 text-left">
      {/* HEADER — mobile only */}
      <div className="md:hidden bg-[#0A4730] rounded-b-[50px] px-6 pt-12 pb-8">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center active:scale-95 transition"
          >
            <ChevronLeft
              className="text-white"
              size={20}
            />
          </button>

          <div>
            <p className="text-[12px] text-white opacity-80 mb-1">
              {unreadCount > 0
                ? `${unreadCount} unread`
                : "All caught up"}
            </p>

            <h1 className="text-2xl text-[#C0F6B7] font-serif font-semibold leading-tight">
              Notifications
            </h1>
          </div>
        </div>
      </div>

      {/* DESKTOP HEADER */}
      <div className="hidden md:flex items-center justify-between max-w-xl mx-auto px-5 pt-10 pb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full border border-slate-200 bg-white flex items-center justify-center active:scale-95 transition text-[#0A4730]/60 hover:border-[#0A4730]/30"
          >
            <ChevronLeft size={18} />
          </button>

          <h1 className="text-2xl text-[#0A4730] font-serif font-semibold">
            Notifications
          </h1>
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-xl mx-auto px-5 py-8 space-y-5">
        {actionError && (
          <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-red-500 text-sm">
            {actionError}
          </div>
        )}

        {unreadCount > 0 && (
          <div className="flex items-center justify-between">
            <SectionLabel>
              {unreadCount} Unread
            </SectionLabel>

            <button
              onClick={handleMarkAllRead}
              disabled={isUpdating}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#0A4730] disabled:opacity-40"
            >
              <CheckCheck size={13} />
              Mark all read
            </button>
          </div>
        )}

        {isLoading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-16 rounded-2xl bg-gray-100 animate-pulse"
              />
            ))}
          </div>
        )}

        {!isLoading &&
          notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-full bg-[#0A4730]/5 flex items-center justify-center mb-4">
                <Bell
                  size={22}
                  className="text-[#0A4730]/30"
                />
              </div>

              <p className="text-sm font-semibold text-[#0A2540]/60">
                No notifications yet
              </p>

              <p className="text-xs text-[#0A2540]/35 mt-1">
                We'll let you know when something
                needs your attention.
              </p>
            </div>
          )}

        {!isLoading &&
          notifications.length > 0 && (
            <>
              <div className="space-y-2.5">
                {notifications.map((n) => (
                  <NotificationCard
                    key={n.id}
                    notification={n}
                    isExpanded={
                      expandedId === n.id
                    }
                    onToggle={() =>
                      handleToggle(n)
                    }
                    onViewTransfer={
                      handleViewTransfer
                    }
                  />
                ))}
              </div>

              {hasNextPage && (
                <button
                  onClick={() =>
                    fetchNextPage()
                  }
                  disabled={
                    isFetchingNextPage
                  }
                  className="w-full h-12 rounded-2xl border border-slate-200 text-sm font-semibold text-[#0A4730] hover:border-[#0A4730]/30 active:scale-[0.98] transition disabled:opacity-50"
                >
                  {isFetchingNextPage
                    ? "Loading…"
                    : "Load more"}
                </button>
              )}
            </>
          )}
      </div>
    </div>
  );
}